import { FC, useEffect, useState } from 'react';
import { Box, Button } from '@mui/material';
import CurrencyRubleIcon from '@mui/icons-material/CurrencyRuble';

import { useAppDispatch, useAppSelector } from 'shared/utils/hooks/useRedux';
import { CaseMainInfo, IdCaseMainInfo, LastFetchTimeMainInfo, LoadingMainInfo } from 'entities/insurance-contract/main-info/selector';
import { fetchCase } from 'entities/insurance-contract/main-info/actions/fetch-main-info';

import styles from 'features/contract-page/ui/styles/MainInfo.module.css';
import CustomButton from 'shared/custom-button/CustomButton';
import { isValidIdCase } from 'shared/utils/hooks/useValidationIdCase';
import Loader from 'shared/loader/Loader';

import PersonIcon from '@mui/icons-material/Person';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import EmailIcon from '@mui/icons-material/Email';
import ClickToCopy from './CopyPhone';

interface IMainInfo {
    idCase: string;
}

const MainInfo: FC<IMainInfo> = ({ idCase }) => {
    const dispatch = useAppDispatch();
    const mainInfoData = useAppSelector(CaseMainInfo);
    const lastFetch = useAppSelector(LastFetchTimeMainInfo);
    const storedIdCase = useAppSelector(IdCaseMainInfo);

    const isLoading = useAppSelector(LoadingMainInfo);

    const [isValid, setIsValid] = useState(true);

    const fetchCaseWithValidation = () => {
        if (isValidIdCase(idCase)) {
            dispatch(fetchCase({ idCase }));
        } else {
            setIsValid(false)
            console.error('Invalid idCase');
        }
    };

    useEffect(() => {
        if (idCase) {
            const currentTime = Date.now();
            const oneDay = 24 * 60 * 60 * 1000;
            const isIdChanged = idCase !== storedIdCase;
            const isDataStale = !lastFetch || (currentTime - lastFetch) > oneDay;

            if (isIdChanged || isDataStale) {
                fetchCaseWithValidation();
            }
        } else {
            console.error('Missing idCase');
        }
    }, [dispatch, idCase, lastFetch, storedIdCase]);

    const handleForceRefresh = () => {
        fetchCaseWithValidation();
    };

    const formatNumber = (number: string) => {
        if (number) {
            if (number.length !== 11) {
                throw new Error('Номер телефона должен содержать 11 цифр');
            }
            const match = number.match(/^(\d)(\d{3})(\d{3})(\d{2})(\d{2})$/);

            if (match) {
                return [match[1], match[2], match[3], match[4], match[5]].join(' ');
            }
            return number;
        }
    }

  const [openDescMap, setOpenDescMap] = useState<{ [key: number]: boolean }>({});
// Функция переключения для конкретного индекса
const toggleOpen = (index: number) => {
  setOpenDescMap(prev => ({
    ...prev,
    [index]: !prev[index],
  }));
};

    if (!isValid) {
        return (
            <p className={styles.errorText}>Некорректный ID договора</p>
        )
    } else {
        return (
            <>
                {isLoading && (
                    <Loader
                        title='Загрузка...'
                    />
                )}
                {!isLoading && (
                    <>
                        <Box className={styles.shadowBox}>
                            <Box className={styles.rightBox}>
                                <h2 className={styles.title}>Данные по договору</h2>
                                {mainInfoData && (
                                    <>
                                        <Box className={styles.contentRow}>
                                            <Box className={styles.contentColumn}><span>Компания/Страхователь</span><p>{mainInfoData.InsurerName}</p></Box>
                                            <Box className={styles.contentColumn}><span>Холдинг</span><p>{mainInfoData.HoldingName ? mainInfoData.HoldingName : '-'}</p></Box>
                                        </Box>
                                        <Box className={styles.contentRow}>
                                            <Box className={styles.contentColumn}><span>Страховая сумма</span><p className={styles.icon}>{mainInfoData.InsuranceSum} <CurrencyRubleIcon /></p> </Box>
                                            <Box className={styles.contentColumn}><span>Страховая премия</span><p className={styles.icon}>{mainInfoData.InsurancePremium} <CurrencyRubleIcon /></p></Box>
                                        </Box>

                                        <Box className={styles.contentRow}>
                                            <Box className={styles.contentColumn}><span>Период действия</span><p>{mainInfoData.StartDate} - {mainInfoData.EndDate}</p></Box>
                                            <Box className={styles.contentColumn}><span>Количество застрахованных</span><p>{mainInfoData.InsuredQuantity ? `${mainInfoData.InsuredQuantity} человек` : '-'}</p></Box>
                                        </Box>
                                    </>
                                )}
                            </Box>
                            <Box className={styles.leftBox}>
                                {mainInfoData.Curators.length ? (
                                    <>
                                        <h2 className={styles.title}>Координация и сопровождение</h2>
                                        {mainInfoData.Curators.map((item, index) => (
                                            <Box className={styles.contentCurator} key={index}>
                                                <span className={`${styles.iconRole} ${item.RoleName === 'Врач куратор ВИП' ? styles.VIPRole : ''}`}>{item.RoleName}</span>
                                                <Box className={`${styles.photoBox} ${item.PhotoDataBase64 ? '' : styles.emptyPhoto}`}>
                                                    {item.PhotoDataBase64 ? (
                                                        <img
                                                            src={`data:image/jpeg;base64,${item.PhotoDataBase64}`}
                                                            alt='Foto'
                                                            className={styles.photoImg}
                                                        />
                                                    ) : (
                                                        <Box className={styles.emptyPhotoIcon}>
                                                            <PersonIcon />
                                                        </Box>
                                                    )}
                                                </Box>
                                                <Box>
                                                    <Box className={styles.curatorInfo}><h4>{item.Name}</h4></Box>
                                                    {item.WorkPhone && (
                                                        <ClickToCopy
                                                            value={`+84959701070 доб.${item.WorkPhone}`}
                                                            displayValue={`8 495 970 10 70 доб. ${item.WorkPhone}`}
                                                            icon={LocalPhoneIcon}
                                                        />
                                                    )}
                                                    <Box className={styles.oneLine}>
                                                        {item.MobilePhone && (
                                                            <ClickToCopy
                                                                value={`+${item.MobilePhone}`}
                                                                displayValue={formatNumber(item.MobilePhone) ?? item.MobilePhone}
                                                                icon={PhoneIphoneIcon}
                                                            />
                                                        )}
                                                        {item.Email && (
                                                            <Box className={styles.curatorInfo}>
                                                                <span><EmailIcon /></span>
                                                                <a href={`mailto:${item.Email}`}>{item.Email}</a>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                    {item.Description && (
                                                        <Box className={styles.btnReadMore}>
                                                            <Button onClick={() => toggleOpen(index)} sx={{textTransform: 'lowercase'}}>
                                                                {openDescMap[index] ? 'Скрыть' : 'Подробнее'}
                                                            </Button>
                                                            <Box sx={{display: openDescMap[index] ? 'block' : 'none'}}>
                                                                <p>{item.Description}</p>
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                        ))}
                                    </>

                                ) : (
                                    <Box className={styles.curatorInfo}><span>Кураторы пока не назначены</span></Box>
                                )}
                            </Box>
                        </Box>
                        <Box className={styles.updateBtnBox}>
                            <CustomButton
                                title="Обновить данные"
                                handleClick={handleForceRefresh}
                                textColor="white"
                                backgroundColor="#0054E6"
                                padding="5px 30px"
                                gap="10px"
                                border='none'
                                borderRadius='4px'
                            />
                        </Box>
                    </>
                )}

            </>
        );
    }


};

export default MainInfo;


